// A paleta do Campus Agzos.
//
// Toda cor de toda arte sai daqui. Cada material é uma RAMPA de 4 tons, do escuro
// ao claro: 0 = contorno/sombra funda, 1 = sombra, 2 = base, 3 = luz. A luz vem do
// alto à esquerda: topo e lado esquerdo levam o tom 3, base e lado direito o tom 1,
// e o contorno externo o tom 0.
//
// Um sprite nunca usa hex solto: usa um TOKEN, que é nome-da-rampa + índice
// ("wood2", "leaf3"). Assim a cena inteira fica coesa e um ajuste aqui muda tudo.

export type Ramp = readonly [string, string, string, string];

export const RAMPS = {
  // madeira escura (mesas, portas) e clara (piso, estantes)
  wood: ['#3a2418', '#6b4429', '#93603a', '#bd8a5c'],
  oak: ['#5a3d24', '#8f6a43', '#b8915f', '#dcbb86'],
  // metal, plástico claro, preto
  metal: ['#262a33', '#4a5160', '#7a8394', '#b4bccb'],
  plastic: ['#7d8290', '#b6bbc6', '#dde0e7', '#f7f8fa'],
  black: ['#0d0e12', '#1a1c22', '#272a32', '#363a45'],
  // identidade Agzos
  red: ['#4f0c10', '#9a171c', '#e03131', '#ff7070'],
  white: ['#aeb2bb', '#d2d5db', '#eceef1', '#ffffff'],
  // tecidos
  fabricBlue: ['#18264a', '#29457a', '#3f6db0', '#74a2d8'],
  fabricGreen: ['#173320', '#2a5a38', '#43875a', '#79bb86'],
  fabricGray: ['#272a32', '#434856', '#666c7c', '#9399a8'],
  fabricMustard: ['#4f3a0c', '#8a6614', '#c2932a', '#e8c05c'],
  // natureza
  leaf: ['#16341c', '#2a6233', '#45934a', '#7cc56a'],
  trunk: ['#2e1c12', '#4f3220', '#714a2f', '#946541'],
  pot: ['#4f2816', '#86472a', '#b5693d', '#d99062'],
  grass: ['#27502a', '#356b35', '#4a8a42', '#67a854'],
  water: ['#163a5c', '#22598a', '#3a86c0', '#7cc0ea'],
  // telas e vidro
  screen: ['#08131f', '#0f2c47', '#2a6fa8', '#86d6ff'],
  glass: ['#3a5a73', '#6690b0', '#a2c6df', '#e0f0fb'],
  // papel, pisos, construção
  paper: ['#958d7c', '#c7bfac', '#eae4d5', '#fffaf0'],
  tile: ['#7a7f8c', '#a2a7b3', '#c7cbd3', '#e6e8ec'],
  concrete: ['#4c5057', '#6a6e76', '#898d95', '#adb1b8'],
  asphalt: ['#202227', '#2c2e34', '#383b42', '#474b53'],
  brick: ['#4f241f', '#7a362d', '#a04a3a', '#c46a54'],
  carpet: ['#4a1a1f', '#6e262c', '#8c343a', '#a8474c'],
  wall: ['#2a2d36', '#d7d1c4', '#e9e4d8', '#f6f2ea'],
  gold: ['#5e420f', '#9a741c', '#d6a62e', '#f6d774'],
  // luzes
  light: ['#7a5a1a', '#d9a23a', '#ffd76a', '#fff4c2'],
} as const satisfies Record<string, Ramp>;

export type RampName = keyof typeof RAMPS;

/** Cores avulsas permitidas como token (olho, boca, brilho). */
export const SINGLES = {
  ink: '#16121c', // olhos, contorno de personagem
  mouth: '#8a3a3a',
  blush: '#e8858a',
  shine: '#ffffff',
  shadow: 'rgba(0,0,0,0.28)', // sombra no chão
} as const;

// ---------------------------------------------------------------- personagens

/** Tons de pele (8), do mais claro ao mais escuro. */
export const SKIN_TONES: readonly Ramp[] = [
  ['#9a6a54', '#e0b294', '#f6d2b4', '#fde6d2'],
  ['#8f5e45', '#d6a07c', '#efc39c', '#f9dcbf'],
  ['#865636', '#c98e62', '#e2ac7f', '#f0c79f'],
  ['#74462a', '#b47848', '#cf9462', '#e3b07f'],
  ['#5e3820', '#9a6236', '#b97c4b', '#d39a66'],
  ['#4a2a18', '#7f4d2a', '#9c633a', '#b97f51'],
  ['#3a2014', '#653a20', '#7f4c2c', '#9c6540'],
  ['#2a170e', '#4c2a17', '#633820', '#7d4c30'],
];

/** Cores de cabelo (10). */
export const HAIR_COLORS: readonly Ramp[] = [
  ['#0c0a0c', '#1c1719', '#2c2427', '#463a3c'], // preto
  ['#1f130c', '#3b2416', '#563521', '#744b31'], // castanho escuro
  ['#3a220f', '#61391a', '#8a5427', '#ad7039'], // castanho
  ['#5a3a12', '#94661f', '#c5913a', '#e6b960'], // loiro escuro
  ['#7a5a1f', '#c49a3c', '#e8c46a', '#f8e2a0'], // loiro
  ['#4f1a0c', '#8c3418', '#bd5226', '#e07a42'], // ruivo
  ['#4a4a52', '#7d7d88', '#adadb8', '#dadae2'], // grisalho
  ['#7a7a84', '#b9b9c2', '#e2e2e8', '#ffffff'], // branco
  ['#2a1450', '#4a2a8a', '#7448c4', '#a47ef0'], // roxo
  ['#0c3a3a', '#14606a', '#1f8f9a', '#4cc6cc'], // azul-petróleo
];

/** Cores de roupa (14): servem para camisa, calça, sapato e acessório. */
export const CLOTH_COLORS: readonly Ramp[] = [
  RAMPS.red,
  RAMPS.fabricBlue,
  RAMPS.fabricGreen,
  RAMPS.fabricGray,
  RAMPS.fabricMustard,
  RAMPS.black,
  RAMPS.white,
  ['#1c2433', '#2b3850', '#3c4d6c', '#56698c'], // marinho
  ['#3a1a40', '#5e2a68', '#8a4294', '#b46cbc'], // vinho/roxo
  ['#4a2614', '#7a4022', '#a85a32', '#cc7c4c'], // terracota
  ['#123a36', '#1d5c54', '#2c8a7c', '#56b8a6'], // verde-água
  ['#5c1c38', '#94305a', '#c84c80', '#ea80a8'], // rosa
  ['#3a3424', '#5e5438', '#857a52', '#ada276'], // cáqui
  ['#1a2a4a', '#2c4a80', '#4a78c0', '#86b0ee'], // azul jeans
];

/**
 * Tokens de slot, só para partes de personagem: a cor depende do Look.
 * skin0..3, hair0..3, shirt0..3, pants0..3, shoe0..3, acc0..3.
 */
export const SLOT_NAMES = ['skin', 'hair', 'shirt', 'pants', 'shoe', 'acc'] as const;
export type SlotName = (typeof SLOT_NAMES)[number];
export type Slots = Record<SlotName, Ramp>;

/** Todos os tokens fixos válidos (rampas + avulsos). */
export function fixedTokens(): Set<string> {
  const out = new Set<string>(Object.keys(SINGLES));
  for (const name of Object.keys(RAMPS)) for (let i = 0; i < 4; i++) out.add(`${name}${i}`);
  return out;
}

/** Todos os tokens de slot (skin0..acc3). */
export function slotTokens(): Set<string> {
  const out = new Set<string>();
  for (const name of SLOT_NAMES) for (let i = 0; i < 4; i++) out.add(`${name}${i}`);
  return out;
}

const TOKEN_RE = /^([a-zA-Z]+)([0-3])$/;

/** Resolve um token para cor. `slots` só é necessário para partes de personagem. */
export function resolveToken(token: string, slots?: Slots): string | undefined {
  if (token in SINGLES) return SINGLES[token as keyof typeof SINGLES];
  const match = TOKEN_RE.exec(token);
  if (!match) return undefined;
  const [, name, idx] = match;
  const i = Number(idx);
  if (name && name in RAMPS) return RAMPS[name as RampName][i];
  if (slots && name && name in slots) return slots[name as SlotName][i];
  return undefined;
}
